import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import {
  CheckCircle,
  Cancel,
  Schedule,
  Info,
  Refresh,
  AdminPanelSettings,
  Check,
  Close
} from '@mui/icons-material';
import { User } from 'firebase/auth';
import {
  getAllStations,
  getAllStationTypes,
  getAllBookings,
  getAllBookingGroups,
  approveBookingGroup,
  rejectBookingGroup,
  isUserAdmin,
  createLockCode,
  Station,
  StationType,
  Booking,
  BookingGroup
} from '../Firebase/firebaseApi.ts';
import CouponsAdminTab from './CouponsAdminTab.tsx';
import CouponCreationDialog from './CouponCreationDialog.tsx';
import { auditLog } from '../Firebase/auditLog.ts';
import { trackEvent } from '../Analytics/posthog.ts';
import { translateApiError } from '../utils/errorTranslations.ts';
import BackgroundWrapper from '../components/BackgroundWrapper.tsx';
import GeneratedCodeDialog from './GeneratedCodeDialog.tsx';
import { hebrewWeekdaysFromNukiMask, nukiWeekdayBitmaskFromDates } from './nukiWeekdays.ts';
import { bookingGroupsById, isBookingApproved } from '../Firebase/bookingGroupUtils.ts';
import './Admin.css';

interface AdminProps {
  user: User | null;
}

interface StationWithDetails extends Station {
  stationType?: StationType;
  currentBookings: Booking[];
  status: 'available' | 'booked' | 'partially-booked';
  nextAvailable?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Admin: React.FC<AdminProps> = ({ user }) => {
  const [stations, setStations] = useState<StationWithDetails[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [bookingGroups, setBookingGroups] = useState<(BookingGroup & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<(BookingGroup & { id: string }) | null>(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckLoading, setAdminCheckLoading] = useState(true);
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [filterFromDate, setFilterFromDate] = useState<Date | null>(new Date());
  const [filterToDate, setFilterToDate] = useState<Date | null>(new Date());
  const [tempCodeDialogOpen, setTempCodeDialogOpen] = useState(false);
  const [tempCodeLoading, setTempCodeLoading] = useState(false);
  const [tempCodeError, setTempCodeError] = useState<string | null>(null);
  const [tempCode, setTempCode] = useState<string | null>(null);

  // Check admin status when user changes
  useEffect(() => {
    const checkAdminStatus = async () => {
      setAdminCheckLoading(true);
      const adminStatus = await isUserAdmin(user?.email);
      setIsAdmin(adminStatus);
      
      if (adminStatus && user?.email) {
        // Track admin page access
        trackEvent('admin_page_accessed', {
          user_email: user.email,
          admin_status: adminStatus,
        });
      } else if (user?.email) {
        // Track unauthorized admin access attempt
        trackEvent('admin_page_access_denied', {
          user_email: user.email,
          admin_status: adminStatus,
        });
      }
      
      setAdminCheckLoading(false);
    };

    if (user) {
      checkAdminStatus();
    } else {
      setIsAdmin(false);
      setAdminCheckLoading(false);
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [stationsData, stationTypesData, bookingsData, groupsData] = await Promise.all([
        getAllStations(),
        getAllStationTypes(),
        getAllBookings(),
        getAllBookingGroups()
      ]);

      setBookings(bookingsData);
      setBookingGroups(groupsData);

      const groupsById = bookingGroupsById(groupsData);

      // Process stations with their current status
      const processedStations: StationWithDetails[] = stationsData.map(station => {
        const stationType = stationTypesData.find(type => type.id === station.typeId);
        const currentBookings = bookingsData.filter(booking =>
          booking.stationId === station.id && isBookingApproved(booking, groupsById)
        );

        // Determine station status
        let status: 'available' | 'booked' | 'partially-booked' = 'available';
        const now = new Date();

        const activeBookings = currentBookings.filter(booking => {
          const fromDate = new Date(booking.fromDate);
          const toDate = new Date(booking.toDate);
          return fromDate <= now && now <= toDate;
        });

        if (activeBookings.length > 0) {
          status = 'booked';
        } else {
          const futureBookings = currentBookings.filter(booking => {
            const fromDate = new Date(booking.fromDate);
            return fromDate > now;
          });
          if (futureBookings.length > 0) {
            status = 'partially-booked';
          }
        }

        // Find next available time
        let nextAvailable: string | undefined;
        const activeBooking = activeBookings[0];
        if (activeBooking) {
          nextAvailable = new Date(activeBooking.toDate).toLocaleString('he-IL');
        }

        return {
          ...station,
          stationType,
          currentBookings,
          status,
          nextAvailable
        };
      });

      setStations(processedStations);

      // Log admin access (simplified to avoid blocking errors)
      try {
        await auditLog.stationsRead(processedStations.length);
      } catch (auditError) {
        console.warn('Audit logging failed, but continuing:', auditError);
      }

    } catch (error) {
      console.error('Error loading admin data:', error);

      // More detailed error message
      let errorMessage = 'שגיאה בטעינת נתוני המנהל';
      if (error instanceof Error) {
        errorMessage += ': ' + error.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && isAdmin && !adminCheckLoading) {
      loadData();
    }
  }, [user, isAdmin, adminCheckLoading]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getGroupBookings = (group: BookingGroup & { id: string }): Booking[] => {
    const ids = new Set(Object.keys(group.bookingIds || {}));
    return bookings
      .filter((b) => b.groupId === group.id || ids.has(String(b.id)))
      .sort((a, b) => new Date(a.fromDate).getTime() - new Date(b.fromDate).getTime());
  };

  const groupsById = bookingGroupsById(bookingGroups);
  const bookingIsApproved = (booking: Booking) => isBookingApproved(booking, groupsById);

  const handleBookingClick = (booking: Booking) => {
    setSelectedBooking(booking);
    setSelectedGroup(null);
    setBookingDialogOpen(true);
  };

  const handleGroupDetailsClick = (group: BookingGroup & { id: string }) => {
    setSelectedGroup(group);
    setSelectedBooking(null);
    setGroupDialogOpen(true);
  };

  const handleApproveGroupClick = (group: BookingGroup & { id: string }, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setSelectedGroup(group);
    setSelectedBooking(null);
    setConfirmAction('approve');
    setConfirmDialogOpen(true);
  };

  const handleRejectGroupClick = (group: BookingGroup & { id: string }, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setSelectedGroup(group);
    setSelectedBooking(null);
    setConfirmAction('reject');
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!confirmAction || !selectedGroup) return;

    setActionLoading(true);
    try {
      const memberBookings = getGroupBookings(selectedGroup);

      if (confirmAction === 'approve') {
        const fromDates = memberBookings.map((b) => new Date(b.fromDate).getTime());
        const toDates = memberBookings.map((b) => new Date(b.toDate).getTime());
        const rangeFrom = new Date(Math.min(...fromDates)).toISOString();
        const rangeTo = new Date(Math.max(...toDates)).toISOString();
        const weekDays = nukiWeekdayBitmaskFromDates(memberBookings.map((b) => b.fromDate));

        let code: string | undefined;
        try {
          code = await createLockCode(
            rangeFrom,
            rangeTo,
            selectedGroup.userEmail,
            false,
            weekDays,
            selectedGroup.id
          );
          setGeneratedCode(code);
          setCodeDialogOpen(true);
        } catch (codeError) {
          console.error('Error generating keypad code:', codeError);
          const translatedError = translateApiError(codeError);
          setError(`הקבוצה אושרה, אך נכשל ביצירת קוד: ${translatedError}`);
        }

        await approveBookingGroup(selectedGroup.id, code);

        trackEvent('admin_booking_group_approved', {
          user_email: user?.email,
          group_id: selectedGroup.id,
          booking_user_email: selectedGroup.userEmail,
          booking_count: memberBookings.length,
          allowed_week_days: weekDays,
        });
      } else {
        await rejectBookingGroup(selectedGroup.id);
        trackEvent('admin_booking_group_rejected', {
          user_email: user?.email,
          group_id: selectedGroup.id,
          booking_user_email: selectedGroup.userEmail,
          booking_count: memberBookings.length,
        });
      }

      await loadData();

      setConfirmDialogOpen(false);
      setBookingDialogOpen(false);
      setGroupDialogOpen(false);
      setSelectedBooking(null);
      setSelectedGroup(null);
      setConfirmAction(null);
    } catch (error) {
      console.error('Error performing action:', error);

      trackEvent('admin_booking_action_failed', {
        user_email: user?.email,
        action: confirmAction,
        group_id: selectedGroup?.id,
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      const translatedError = translateApiError(error);
      setError(`שגיאה ב${confirmAction === 'reject' ? 'דחיית' : 'אישור'} ההזמנה: ${translatedError}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelAction = () => {
    setConfirmDialogOpen(false);
    setConfirmAction(null);
    setSelectedGroup(null);
  };

  const handleCreateTempCode = async () => {
    if (!user?.email) {
      setTempCodeError('נדרשת התחברות למערכת');
      return;
    }

    setTempCodeLoading(true);
    setTempCodeError(null);
    setTempCode(null);

    try {
      // Create a 1-day temp code starting from now
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Format dates as ISO strings
      const fromDate = now.toISOString();
      const toDate = tomorrow.toISOString();

      // Create lock code with skipPendingRequest = true
      const code = await createLockCode(fromDate, toDate, user.email, true);

      setTempCode(code);
      setTempCodeDialogOpen(true);

      // Track temp code creation
      trackEvent('admin_temp_code_created', {
        user_email: user.email,
        from_date: fromDate,
        to_date: toDate,
      });
    } catch (error) {
      console.error('Error creating temp code:', error);
      const translatedError = translateApiError(error);
      setTempCodeError(`נכשל ביצירת קוד זמני: ${translatedError}`);
      setTempCodeDialogOpen(true);

      // Track temp code creation failure (keep original English for logging)
      const originalMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorCode = (error as any)?.code || 'UNKNOWN';
      trackEvent('admin_temp_code_failed', {
        user_email: user.email,
        error: originalMessage,
        errorCode: errorCode,
      });
    } finally {
      setTempCodeLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'booked':
        return 'error';
      case 'partially-booked':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available':
        return <CheckCircle />;
      case 'booked':
        return <Cancel />;
      case 'partially-booked':
        return <Schedule />;
      default:
        return <Info />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available':
        return 'זמינה';
      case 'booked':
        return 'תפוסה';
      case 'partially-booked':
        return 'הזמנות עתידיות';
      default:
        return 'לא ידוע';
    }
  };

  if (!user) {
    return (
      <BackgroundWrapper>
        <Container maxWidth="md" sx={{ py: 8, direction: 'rtl' }}>
          <Alert severity="warning">
            נדרשת התחברות למערכת
          </Alert>
        </Container>
      </BackgroundWrapper>
    );
  }

  if (adminCheckLoading) {
    return (
      <BackgroundWrapper>
        <Container maxWidth="lg" sx={{ py: 8, direction: 'rtl', textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ mt: 2, color: 'white', textShadow: "2px 2px 4px rgba(0,0,0,0.7)" }}>
            בודק הרשאות...
          </Typography>
        </Container>
      </BackgroundWrapper>
    );
  }

  if (!isAdmin) {
    return (
      <BackgroundWrapper>
        <Container maxWidth="md" sx={{ py: 8, direction: 'rtl' }}>
          <Alert severity="error">
            אין לך הרשאות מנהל לגישה לעמוד זה
          </Alert>
        </Container>
      </BackgroundWrapper>
    );
  }

  if (loading) {
    return (
      <BackgroundWrapper>
        <Container maxWidth="lg" sx={{ py: 8, direction: 'rtl', textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ mt: 2, color: 'white', textShadow: "2px 2px 4px rgba(0,0,0,0.7)" }}>
            טוען נתוני מנהל...
          </Typography>
        </Container>
      </BackgroundWrapper>
    );
  }

  if (error) {
    return (
      <BackgroundWrapper>
        <Container maxWidth="md" sx={{ py: 8, direction: 'rtl' }}>
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={loadData}>
              נסה שוב
            </Button>
          }>
            {error}
          </Alert>
        </Container>
      </BackgroundWrapper>
    );
  }

  const availableStations = stations.filter(s => s.status === 'available');
  const bookedStations = stations.filter(s => s.status === 'booked');
  const partiallyBookedStations = stations.filter(s => s.status === 'partially-booked');
  const pendingGroups = bookingGroups.filter((g) => !g.approved);

  return (
    <BackgroundWrapper>
      <Container maxWidth="lg" sx={{ py: 4, direction: 'rtl' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <AdminPanelSettings sx={{ fontSize: 40, mr: 2, color: 'white' }} />
          <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 'bold', textShadow: "2px 2px 4px rgba(0,0,0,0.7)" }}>
            פאנל מנהל
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mr: 'auto' }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadData}
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                },
                '& .MuiButton-startIcon': {
                  marginLeft: '12px',
                  marginRight: '0px'
                }
              }}
            >
              רענן נתונים
            </Button>
            <Button
              variant="contained"
              startIcon={tempCodeLoading ? <CircularProgress size={20} color="inherit" /> : <Info />}
              onClick={handleCreateTempCode}
              disabled={tempCodeLoading}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)'
                },
                '& .MuiButton-startIcon': {
                  marginLeft: '12px',
                  marginRight: '0px'
                }
              }}
            >
              {tempCodeLoading ? 'יוצר קוד...' : 'צור קוד זמני ליום'}
            </Button>
          </Box>
      </Box>

        {/* Statistics Cards */}
        <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
          <Card sx={{ flex: '1 1 250px', minWidth: 250, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {availableStations.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                תחנות זמינות
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 250px', minWidth: 250, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Cancel sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
              <Typography variant="h4" color="error.main">
                {bookedStations.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                תחנות תפוסות
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 250px', minWidth: 250, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Schedule sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {partiallyBookedStations.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                הזמנות עתידיות
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 250px', minWidth: 250, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Info sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" color="info.main">
                {pendingGroups.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                הזמנות ממתינות
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px 8px 0 0' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="סטטוס תחנות" />
            <Tab label="הזמנות" />
            <Tab label="הזמנות ממתינות" />
            <Tab label="קופונים" />
            <Tab label="אנליטיקה" />
          </Tabs>
        </Box>

        {/* Station Status Tab */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            <Table>
            <TableHead>
              <TableRow>
                <TableCell>עמדה</TableCell>
                <TableCell>סוג</TableCell>
                <TableCell>סטטוס</TableCell>
                <TableCell>הזמנות פעילות</TableCell>
                <TableCell>זמין הבא</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stations.map((station) => (
                <TableRow key={station.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={station.imageLink}
                        sx={{ width: 40, height: 40, mr: 2 }}
                      />
                      <Typography variant="body2">
                        עמדה {station.id}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {station.stationType?.typeFriendlyName || 'לא ידוע'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(station.status)}
                      label={getStatusText(station.status)}
                      color={getStatusColor(station.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {station.currentBookings.length}
                  </TableCell>
                  <TableCell>
                    {station.nextAvailable || 'זמינה כעת'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* All Bookings Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              סינון לפי תאריכים
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <DatePicker
                  label="מתאריך"
                  value={filterFromDate}
                  onChange={(newValue) => setFilterFromDate(newValue)}
                  slotProps={{
                    textField: {
                      sx: { flex: '1 1 250px', minWidth: 250 }
                    }
                  }}
                />
                <DatePicker
                  label="עד תאריך"
                  value={filterToDate}
                  onChange={(newValue) => setFilterToDate(newValue)}
                  slotProps={{
                    textField: {
                      sx: { flex: '1 1 250px', minWidth: 250 }
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFilterFromDate(null);
                    setFilterToDate(null);
                  }}
                  sx={{ alignSelf: 'center' }}
                >
                  נקה סינון
                </Button>
              </Box>
            </LocalizationProvider>
          </Box>
          <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            <Table>
            <TableHead>
              <TableRow>
                <TableCell>עמדה</TableCell>
                <TableCell>משתמש</TableCell>
                <TableCell>תאריך התחלה</TableCell>
                <TableCell>תאריך סיום</TableCell>
                <TableCell>סטטוס</TableCell>
                <TableCell>פעולות</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings
                .filter((booking) => {
                  // Filter by date range if specified
                  const bookingFromDate = new Date(booking.fromDate);
                  const bookingToDate = new Date(booking.toDate);

                  if (filterFromDate && bookingToDate < filterFromDate) {
                    return false;
                  }
                  if (filterToDate) {
                    const filterToDateEndOfDay = new Date(filterToDate);
                    filterToDateEndOfDay.setHours(23, 59, 59, 999);
                    if (bookingFromDate > filterToDateEndOfDay) {
                      return false;
                    }
                  }
                  return true;
                })
                .map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell>
                      עמדה {booking.stationId}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {booking.userName || booking.userEmail}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {booking.userEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(booking.fromDate).toLocaleString('he-IL')}
                    </TableCell>
                    <TableCell>
                      {new Date(booking.toDate).toLocaleString('he-IL')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bookingIsApproved(booking) ? 'מאושרת' : 'ממתינה לאישור'}
                        color={bookingIsApproved(booking) ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {!bookingIsApproved(booking) && (
                          <Chip label="אישור דרך קבוצה" size="small" variant="outlined" />
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleBookingClick(booking)}
                        >
                          פרטים
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
              ))}
            </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Pending Groups Tab */}
        <TabPanel value={tabValue} index={2}>
          <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            <Table>
            <TableHead>
              <TableRow>
                <TableCell>משתמש</TableCell>
                <TableCell>מס׳ הזמנות</TableCell>
                <TableCell>תאריך ראשון</TableCell>
                <TableCell>תאריך אחרון</TableCell>
                <TableCell>עמדות</TableCell>
                <TableCell>סטטוס</TableCell>
                <TableCell>פעולות</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingGroups.map((group) => {
                const members = getGroupBookings(group);
                const stationIds = [...new Set(members.map((b) => b.stationId))].sort(
                  (a, b) => Number(a) - Number(b)
                );
                const firstFrom = members[0]?.fromDate;
                const lastTo = members[members.length - 1]?.toDate;
                return (
                  <TableRow key={group.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {group.userName || group.userEmail}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {group.userEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{members.length}</TableCell>
                    <TableCell>
                      {firstFrom ? new Date(firstFrom).toLocaleDateString('he-IL') : '—'}
                    </TableCell>
                    <TableCell>
                      {lastTo ? new Date(lastTo).toLocaleDateString('he-IL') : '—'}
                    </TableCell>
                    <TableCell>
                      {stationIds.length > 0
                        ? stationIds.map((id) => `עמדה ${id}`).join(', ')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip label="ממתינה לאישור" color="warning" size="small" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Info />}
                          onClick={() => handleGroupDetailsClick(group)}
                        >
                          פרטים
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<Check />}
                          onClick={() => handleApproveGroupClick(group)}
                          disabled={actionLoading}
                        >
                          אשר
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          startIcon={<Close />}
                          onClick={() => handleRejectGroupClick(group)}
                          disabled={actionLoading}
                        >
                          דחה
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Coupons Tab */}
        <TabPanel value={tabValue} index={3}>
          <CouponsAdminTab isAdmin={isAdmin} />
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ textAlign: 'center', mb: 3, backgroundColor: 'rgba(255, 255, 255, 0.95)', p: 3, borderRadius: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              אנליטיקה ודוחות
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              נתוני שימוש ופעילות במערכת
            </Typography>
          </Box>
          <Box sx={{ width: '100%', height: '400px', border: '1px solid #e0e0e0', borderRadius: 1, position: 'relative', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
          <iframe width="100%" height="400" frameborder="0" allowfullscreen src="https://us.posthog.com/embedded/cqW9M3x_IWUKaGadOu_wMfqpL4ZzYQ" key="0"></iframe>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'text.secondary',
                fontSize: '0.875rem',
                pointerEvents: 'none'
              }}
            >
              טוען נתוני אנליטיקה...
            </Box>
          </Box>
        </TabPanel>

      {/* Booking Details Dialog */}
      <Dialog
        open={bookingDialogOpen}
        onClose={() => setBookingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ direction: 'rtl' }}
      >
        <DialogTitle>
          פרטי הזמנה
        </DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body1">
                <strong>מזהה הזמנה:</strong> {selectedBooking.id}
              </Typography>
              <Typography variant="body1">
                <strong>עמדה:</strong> {selectedBooking.stationId}
              </Typography>
              <Typography variant="body1">
                <strong>משתמש:</strong> {selectedBooking.userName || selectedBooking.userEmail}
              </Typography>
              <Typography variant="body1">
                <strong>אימייל:</strong> {selectedBooking.userEmail}
              </Typography>
              {selectedBooking.phoneNumber && (
                <Typography variant="body1">
                  <strong>מספר טלפון:</strong> {selectedBooking.phoneNumber}
                </Typography>
              )}
              <Typography variant="body1">
                <strong>תאריך התחלה:</strong> {new Date(selectedBooking.fromDate).toLocaleString('he-IL')}
              </Typography>
              <Typography variant="body1">
                <strong>תאריך סיום:</strong> {new Date(selectedBooking.toDate).toLocaleString('he-IL')}
              </Typography>
              <Typography variant="body1">
                <strong>סטטוס:</strong>
                <Chip
                  label={bookingIsApproved(selectedBooking) ? 'מאושרת' : 'ממתינה לאישור'}
                  color={bookingIsApproved(selectedBooking) ? 'success' : 'warning'}
                  size="small"
                  sx={{ mr: 1 }}
                />
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingDialogOpen(false)}>
            סגור
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Details Dialog */}
      <Dialog
        open={groupDialogOpen}
        onClose={() => {
          setGroupDialogOpen(false);
          setSelectedGroup(null);
        }}
        maxWidth="sm"
        fullWidth
        sx={{ direction: 'rtl' }}
      >
        <DialogTitle>פרטי קבוצת הזמנות</DialogTitle>
        <DialogContent>
          {selectedGroup && (() => {
            const members = getGroupBookings(selectedGroup);
            const stationIds = [...new Set(members.map((b) => b.stationId))].sort(
              (a, b) => Number(a) - Number(b)
            );
            const first = members[0];
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Typography variant="body1">
                  <strong>משתמש:</strong> {selectedGroup.userName || selectedGroup.userEmail}
                </Typography>
                <Typography variant="body1">
                  <strong>אימייל:</strong> {selectedGroup.userEmail}
                </Typography>
                {selectedGroup.phoneNumber && (
                  <Typography variant="body1">
                    <strong>טלפון:</strong> {selectedGroup.phoneNumber}
                  </Typography>
                )}
                <Typography variant="body1">
                  <strong>עמדות:</strong>{' '}
                  {stationIds.length > 0
                    ? stationIds.map((id) => `עמדה ${id}`).join(', ')
                    : '—'}
                </Typography>
                {first && (
                  <Typography variant="body1">
                    <strong>שעות:</strong>{' '}
                    {new Date(first.fromDate).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                    {' – '}
                    {new Date(first.toDate).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                )}
                {members.length > 1 && (
                  <Box>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>סדרה — {members.length} מועדים:</strong>
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        maxHeight: 280,
                        overflowY: 'auto',
                      }}
                    >
                      {members.map((b) => (
                        <Chip
                          key={b.id}
                          label={new Date(b.fromDate).toLocaleDateString('he-IL')}
                          variant="outlined"
                          size="small"
                          sx={{ direction: 'ltr' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          {selectedGroup && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<Check />}
                onClick={() => {
                  setGroupDialogOpen(false);
                  handleApproveGroupClick(selectedGroup);
                }}
                disabled={actionLoading}
              >
                אשר
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<Close />}
                onClick={() => {
                  setGroupDialogOpen(false);
                  handleRejectGroupClick(selectedGroup);
                }}
                disabled={actionLoading}
                sx={{ mr: 1 }}
              >
                דחה
              </Button>
            </>
          )}
          <Button
            onClick={() => {
              setGroupDialogOpen(false);
              setSelectedGroup(null);
            }}
          >
            סגור
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelAction}
        maxWidth="sm"
        fullWidth
        sx={{ direction: 'rtl' }}
      >
        <DialogTitle>
          {confirmAction === 'approve' ? 'אישור קבוצת הזמנות' : 'דחיית קבוצת הזמנות'}
        </DialogTitle>
        <DialogContent>
          {selectedGroup && (
            <Box sx={{ mt: 1 }}>
              {(() => {
                const members = getGroupBookings(selectedGroup);
                const weekDays = nukiWeekdayBitmaskFromDates(members.map((b) => b.fromDate));
                const fromDates = members.map((b) => new Date(b.fromDate).getTime());
                const toDates = members.map((b) => new Date(b.toDate).getTime());
                const rangeFrom = members.length
                  ? new Date(Math.min(...fromDates)).toLocaleDateString('he-IL')
                  : '';
                const rangeTo = members.length
                  ? new Date(Math.max(...toDates)).toLocaleDateString('he-IL')
                  : '';
                return (
                  <>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {confirmAction === 'approve'
                        ? `לאשר את הקבוצה (${members.length} הזמנות) עם קוד Nuki אחד? פעיל בימים: `
                        : `לדחות ולמחוק את כל ${members.length} ההזמנות בקבוצה? `}
                      {confirmAction === 'approve' && (
                        <>
                          <strong>{hebrewWeekdaysFromNukiMask(weekDays)}</strong>
                          {' '}בין {rangeFrom} ל־{rangeTo}.
                        </>
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>משתמש:</strong> {selectedGroup.userName || selectedGroup.userEmail}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{members.length} מועדים:</strong>
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        maxHeight: 220,
                        overflowY: 'auto',
                        mb: 1,
                      }}
                    >
                      {members.map((b) => (
                        <Chip
                          key={b.id}
                          label={`${new Date(b.fromDate).toLocaleDateString('he-IL')} · עמדה ${b.stationId}`}
                          variant="outlined"
                          size="small"
                          sx={{ direction: 'ltr' }}
                        />
                      ))}
                    </Box>
                  </>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color={confirmAction === 'reject' ? 'error' : 'success'}
            onClick={handleConfirmAction}
            disabled={actionLoading || !selectedGroup}
            startIcon={actionLoading ? <CircularProgress size={20} /> : (confirmAction === 'reject' ? <Close /> : <Check />)}
          >
            {actionLoading
              ? 'מבצע...'
              : confirmAction === 'approve'
                ? `אשר קבוצה (${selectedGroup ? getGroupBookings(selectedGroup).length : 0})`
                : 'דחה'}
          </Button>
          <Button onClick={handleCancelAction} disabled={actionLoading}>
            ביטול
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generated Code Dialog */}
      <GeneratedCodeDialog
        open={codeDialogOpen}
        code={generatedCode}
        onClose={() => {
          setCodeDialogOpen(false);
          setGeneratedCode(null);
        }}
      />

      {/* Temp Code Dialog */}
      <Dialog
        open={tempCodeDialogOpen}
        onClose={() => {
          setTempCodeDialogOpen(false);
          setTempCode(null);
          setTempCodeError(null);
        }}
        maxWidth="sm"
        fullWidth
        sx={{ direction: 'rtl' }}
      >
        <DialogTitle>
          {tempCode ? 'קוד זמני נוצר בהצלחה' : 'שגיאה ביצירת קוד זמני'}
        </DialogTitle>
        <DialogContent>
          {tempCode ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                הקוד הזמני נוצר בהצלחה ויהיה תקף למשך 24 שעות
              </Alert>
              <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                קוד הכניסה:
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'monospace',
                  textAlign: 'center',
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  fontWeight: 'bold',
                  letterSpacing: 2
                }}
              >
                {tempCode}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                הקוד תקף עד: {new Date(new Date().setDate(new Date().getDate() + 1)).toLocaleString('he-IL')}
              </Typography>
            </Box>
          ) : tempCodeError ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {tempCodeError}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                הקוד לא נוצר. אנא נסה שוב מאוחר יותר או בדוק את חיבור המנעול.
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTempCodeDialogOpen(false);
              setTempCode(null);
              setTempCodeError(null);
            }}
            variant="contained"
          >
            סגור
          </Button>
        </DialogActions>
      </Dialog>

      </Container>
    </BackgroundWrapper>
  );
};

export default Admin;
